import React, { useEffect, useState, createElement } from 'react';
import { X, Download, Eye, EyeOff, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnalytics } from '../context/AnalyticsContext';
export const AnalyticsPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const {
    visitorData,
    exportAnalytics
  } = useAnalytics();
  // Simple password protection (in production, use proper auth)
  const ADMIN_PASSWORD = 'trung2024'; // Change this!
  const handleAuth = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
    } else {
      alert('Incorrect password');
    }
  };
  const handleDownload = () => {
    const data = exportAnalytics();
    const blob = new Blob([data], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitor-analytics-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };
  // Keyboard shortcut: Ctrl+Shift+A to open panel
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setIsOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  if (!visitorData) return null;
  return <>
      {/* Hidden trigger button (only visible on hover in corner) */}
      <button onClick={() => setIsOpen(true)} className="fixed bottom-4 left-4 opacity-0 hover:opacity-100 transition-opacity duration-300 z-50 p-2 bg-gray-800 text-white rounded-full" title="Analytics Panel (Ctrl+Shift+A)" data-id="element-136">
        <BarChart3 size={20} data-id="element-137" />
      </button>

      {/* Analytics Panel */}
      <AnimatePresence data-id="element-138">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-139">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-gray-200 dark:border-gray-700" data-id="element-140">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 flex items-center justify-between text-white" data-id="element-141">
                <div className="flex items-center gap-2" data-id="element-142">
                  <BarChart3 size={24} data-id="element-143" />
                  <h2 className="text-xl font-bold" data-id="element-144">Visitor Analytics</h2>
                </div>
                <button onClick={() => {
              setIsOpen(false);
              setIsAuthenticated(false);
              setPassword('');
            }} className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded transition-colors" data-id="element-145">
                  <X size={20} data-id="element-146" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-147">
                {!isAuthenticated ? <div className="space-y-4" data-id="element-148">
                    <p className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-149">
                      Enter admin password to view analytics
                    </p>
                    <div className="relative" data-id="element-150">
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAuth()} placeholder="Admin password" className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none" autoFocus data-id="element-151" />
                      <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-152">
                        {showPassword ? <EyeOff size={18} data-id="element-153" /> : <Eye size={18} data-id="element-154" />}
                      </button>
                    </div>
                    <button onClick={handleAuth} className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:from-purple-700 hover:to-pink-700 transition-all" data-id="element-155">
                      Unlock Analytics
                    </button>
                  </div> : <div className="space-y-6" data-id="element-156">
                    {/* User Info */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800" data-id="element-157">
                      <h3 className="font-bold text-blue-900 dark:text-blue-100 mb-3" data-id="element-158">
                        Visitor Information
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-159">
                        <div data-id="element-160">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-161">
                            Name:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-162">
                            {visitorData.name || 'Anonymous'}
                          </p>
                        </div>
                        <div data-id="element-163">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-164">
                            Role:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-165">
                            {visitorData.role || 'N/A'}
                          </p>
                        </div>
                        <div data-id="element-166">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-167">
                            Firm:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-168">
                            {visitorData.firm || 'N/A'}
                          </p>
                        </div>
                        <div data-id="element-169">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-170">
                            Language:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-171">
                            {visitorData.language}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800" data-id="element-172">
                      <h3 className="font-bold text-green-900 dark:text-green-100 mb-3" data-id="element-173">
                        Location & Device
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-174">
                        <div data-id="element-175">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-176">
                            IP:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white font-mono text-xs" data-id="element-177">
                            {visitorData.ipAddress || 'Unknown'}
                          </p>
                        </div>
                        <div data-id="element-178">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-179">
                            City:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-180">
                            {visitorData.city || 'Unknown'}
                          </p>
                        </div>
                        <div data-id="element-181">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-182">
                            Country:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-183">
                            {visitorData.country || 'Unknown'}
                          </p>
                        </div>
                        <div data-id="element-184">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-185">
                            Timezone:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-186">
                            {visitorData.timezone || 'Unknown'}
                          </p>
                        </div>
                        <div data-id="element-187">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-188">
                            Device:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-189">
                            {visitorData.device}
                          </p>
                        </div>
                        <div data-id="element-190">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-191">
                            Browser:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-192">
                            {visitorData.browser}
                          </p>
                        </div>
                        <div data-id="element-193">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-194">
                            OS:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-195">
                            {visitorData.os}
                          </p>
                        </div>
                        <div data-id="element-196">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-197">
                            Screen:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-198">
                            {visitorData.screenResolution}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Session Stats */}
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800" data-id="element-199">
                      <h3 className="font-bold text-purple-900 dark:text-purple-100 mb-3" data-id="element-200">
                        Session Statistics
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-201">
                        <div data-id="element-202">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-203">
                            Visit Count:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-204">
                            {visitorData.visitCount}
                          </p>
                        </div>
                        <div data-id="element-205">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-206">
                            Total Time:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-207">
                            {formatTime(visitorData.totalTimeSpent)}
                          </p>
                        </div>
                        <div data-id="element-208">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-209">
                            Current Session:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-210">
                            {formatTime(visitorData.currentSessionTime)}
                          </p>
                        </div>
                        <div data-id="element-211">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-212">
                            First Visit:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white text-xs" data-id="element-213">
                            {new Date(visitorData.firstVisit).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Behavior */}
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800" data-id="element-214">
                      <h3 className="font-bold text-orange-900 dark:text-orange-100 mb-3" data-id="element-215">
                        Behavior & Engagement
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-216">
                        <div data-id="element-217">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-218">
                            Chat Messages:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-219">
                            {visitorData.chatInteractions}
                          </p>
                        </div>
                        <div data-id="element-220">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-221">
                            Files Uploaded:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-222">
                            {visitorData.filesUploaded}
                          </p>
                        </div>
                        <div data-id="element-223">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-224">
                            General Q's:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-225">
                            {visitorData.generalQuestions}
                          </p>
                        </div>
                        <div data-id="element-226">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-227">
                            Trung Q's:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-228">
                            {visitorData.trungQuestions}
                          </p>
                        </div>
                        <div className="col-span-2" data-id="element-229">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-230">
                            Sections Viewed:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-231">
                            {visitorData.sectionsViewed.length > 0 ? visitorData.sectionsViewed.join(', ') : 'None yet'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Referral */}
                    {(visitorData.referrer !== 'Direct' || visitorData.utmSource) && <div className="bg-pink-50 dark:bg-pink-900/20 rounded-xl p-4 border border-pink-200 dark:border-pink-800" data-id="element-232">
                        <h3 className="font-bold text-pink-900 dark:text-pink-100 mb-3" data-id="element-233">
                          Referral Information
                        </h3>
                        <div className="space-y-2 text-sm" data-id="element-234">
                          <div data-id="element-235">
                            <span className="text-gray-600 dark:text-gray-400" data-id="element-236">
                              Referrer:
                            </span>
                            <p className="font-medium text-gray-900 dark:text-white break-all" data-id="element-237">
                              {visitorData.referrer}
                            </p>
                          </div>
                          {visitorData.utmSource && <div data-id="element-238">
                              <span className="text-gray-600 dark:text-gray-400" data-id="element-239">
                                UTM Source:
                              </span>
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-240">
                                {visitorData.utmSource}
                              </p>
                            </div>}
                          {visitorData.utmMedium && <div data-id="element-241">
                              <span className="text-gray-600 dark:text-gray-400" data-id="element-242">
                                UTM Medium:
                              </span>
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-243">
                                {visitorData.utmMedium}
                              </p>
                            </div>}
                          {visitorData.utmCampaign && <div data-id="element-244">
                              <span className="text-gray-600 dark:text-gray-400" data-id="element-245">
                                UTM Campaign:
                              </span>
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-246">
                                {visitorData.utmCampaign}
                              </p>
                            </div>}
                        </div>
                      </div>}

                    {/* Download Button */}
                    <button onClick={handleDownload} className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2" data-id="element-247">
                      <Download size={18} data-id="element-248" />
                      Download Full Analytics JSON
                    </button>
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};